import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  NgZone,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import {
  MatFormField,
  MatHint,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  SiaInitDirective,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { Guid } from 'guid-typescript';
import { take } from 'rxjs/operators';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'sia-text-box',
  templateUrl: './sia-text-box.component.html',
  styleUrls: ['./sia-text-box.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    SiaTooltipDirective,
    SiaInitDirective,
    MatHint,
    SiaErrorHintComponent,
    MatIconButton,
    MatSuffix,
    MatIcon,
    SiaTranslatePipe,
    CdkTextareaAutosize,
  ],
})
export class SiaTextBoxComponent extends SiaBaseEditorSignalComponent {
  guid: string = Guid.create().toString();
  hide = signal(false);

  password = input(false);
  placeholder = input<string | undefined>();

  regex = input<RegExp>();
  regexError = input<string>();

  allowBreakLine = input(false);
  enterPressed = output<void>();

  @ViewChild('autosize') autosize?: CdkTextareaAutosize;

  private _updateSizeTimeout?: any;
  private _updateSizeRequests: number = 0;
  private _inputElement?: HTMLElement | null;
  private _timeouts: any[] = [];
  private _manageKeyDownBound = this.manageKeyDown.bind(this);

  constructor(private _ngZone: NgZone) {
    super();

    effect(() => {
      const v = this.value();
      // Trigger resize when value changes
      if (this.autosize) {
        setTimeout(() => {
          this.autosize?.resizeToFitContent(true);
        });
      }
    });

    // Initialize hide based on password
    effect(
      () => {
        this.hide.set(this.password());
      },
      { allowSignalWrites: true },
    );

    // Handle break line setup
    effect(() => {
      const v = this.allowBreakLine();
      const id = this.id();
      // When either changes, re-setup.
      this.addBreakLineEvent();
    });

    // Handle Value changes (Validation & Resize)
    effect(
      () => {
        const v = this.value();
        const r = this.regex();

        if (r) {
          let currentMap = this.statusMessages() ?? new Map<string, string>();
          // We use a copy or mutate? Model returns the value.
          // To be safe and reactive, we should clone if we modify, or use set/update.

          let shouldUpdate = false;
          if (v && !r.test(v)) {
            if (
              currentMap.get('regex') !== (this.regexError() ?? 'Non valido')
            ) {
              currentMap.set('regex', this.regexError() ?? 'Non valido');
              shouldUpdate = true;
              this.status.set('error');
            }
          } else {
            if (currentMap.has('regex')) {
              currentMap.delete('regex');
              shouldUpdate = true;
              this.status.set(null);
            }
          }

          if (shouldUpdate) {
            // Force update map reference to trigger changes if needed
            this.statusMessages.set(new Map(currentMap));
          }
        }
      },
      { allowSignalWrites: true },
    );
  }

  addBreakLineEvent() {
    if (!this._inputElement && (this.id() || this.guid)) {
      this._inputElement = document.getElementById(this.id() ?? this.guid);
    }
    if (!this._inputElement) {
      let t = setTimeout(() => {
        this.addBreakLineEvent();
      }, 100);
      this._timeouts.push(t);
      return;
    }
    this._inputElement?.removeEventListener(
      'keydown',
      this._manageKeyDownBound,
    );
    if (!this.allowBreakLine()) {
      this._inputElement?.addEventListener('keydown', this._manageKeyDownBound);
    }
  }

  private manageKeyDown(e: KeyboardEvent) {
    // Enter pressed
    if (this.allowBreakLine()) {
      return;
    }
    if (e.key === 'Enter' || e.code == 'Enter') {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.enterPressed.emit();
    }
  }

  initTextBox() {
    this.addBreakLineEvent();
    this.triggerResize();
  }

  private triggerResize() {
    // Wait for Angular to stabilize, then force resize
    this._ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.autosize?.resizeToFitContent(true);
    });
  }

  onBlur() {
    const v = this.value();
    if (v && typeof v === 'string') {
      this.value.set(v.trim());
    }
  }

  override ngOnDestroy(): void {
    this._inputElement?.removeEventListener(
      'keydown',
      this._manageKeyDownBound,
    );
    this._inputElement = null;

    clearTimeout(this._updateSizeTimeout);
    // this._valueChangeTimeout handled by base effect cleanup

    this._timeouts?.forEach((t) => {
      clearTimeout(t);
    });
    this._timeouts = [];
    super.ngOnDestroy();
  }

  visibility() {
    this.hide.update((h) => !h);
  }
}
